import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createRoot } from 'react-dom/client'
import { Extension } from '@tiptap/core'
import Suggestion from '@tiptap/suggestion'
import i18next from 'i18next'
import {
  AtSign,
  ChevronRight,
  CircleDot,
  Code as CodeIcon,
  FilePlus,
  Link2,
  List as ListIcon,
  ListOrdered,
  ListTodo,
  MessageSquareWarning,
  Minus,
  Pilcrow,
  Quote,
  Table as TableIcon,
} from 'lucide-react'
import { createTaskFromCurrentLine, getEditorActions, insertEntityLink } from './editorActions'
import type { Editor, Range } from '@tiptap/core'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import type { JSX } from 'react'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { cn } from '@/lib/utils'

type SlashGroup = 'moly' | 'basic' | 'blocks'

interface SlashCommandItem {
  key: string
  label: string
  description?: string
  shortcut?: string
  keywords: Array<string>
  icon?: JSX.Element
  group: SlashGroup
  /** Hide the item when this editor can't perform it. */
  isAvailable?: (editor: Editor) => boolean
  command: (props: { editor: Editor; range: Range }) => void
}

const tr = (key: string, options?: Record<string, unknown>) => i18next.t(`common:editor.${key}`, options)

const hasExtension = (editor: Editor, name: string) =>
  editor.extensionManager.extensions.some((extension) => extension.name === name)

/**
 * Slash menu items (design 7a). Moly's own blocks come first, because they're
 * what separates this from a plain notes app; typing filters across groups.
 * Built per query so labels follow the current language.
 */
function createSlashMenuItems(): Array<SlashCommandItem> {
  const headings: Array<{ level: 1 | 2 | 3 | 4; shortcut: string }> = [
    { level: 1, shortcut: '⌘ ⇧ 1' },
    { level: 2, shortcut: '⌘ ⇧ 2' },
    { level: 3, shortcut: '⌘ ⇧ 3' },
    { level: 4, shortcut: '⌘ ⇧ 4' },
  ]

  return [
    // --- Moly ---
    {
      key: 'task',
      label: tr('slash.task'),
      description: tr('slash.taskDesc'),
      keywords: ['task', 'todo', 'issue', 'work'],
      icon: <CircleDot className="h-4 w-4" />,
      group: 'moly',
      isAvailable: (editor) => !!getEditorActions(editor).createTask && hasExtension(editor, 'entityLink'),
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run()
        void createTaskFromCurrentLine(editor, tr('untitledTask'))
      },
    },
    {
      key: 'sub-page',
      label: tr('slash.subPage'),
      description: tr('slash.subPageDesc'),
      keywords: ['page', 'subpage', 'doc', 'nest'],
      icon: <FilePlus className="h-4 w-4" />,
      group: 'moly',
      isAvailable: (editor) => !!getEditorActions(editor).createSubPage && hasExtension(editor, 'entityLink'),
      command: ({ editor, range }) => {
        const { createSubPage } = getEditorActions(editor)
        if (!createSubPage) return
        editor.chain().focus().deleteRange(range).run()
        void createSubPage(tr('untitledPage')).then((created) => {
          if (created) insertEntityLink(editor, created, ENTITY_TYPES.DOCUMENT)
        })
      },
    },
    {
      key: 'link',
      label: tr('slash.linkDoc'),
      description: tr('slash.linkDocDesc'),
      keywords: ['link', 'doc', 'page', 'task', 'reference'],
      icon: <Link2 className="h-4 w-4" />,
      group: 'moly',
      isAvailable: (editor) => hasExtension(editor, 'entityLink'),
      // Hand over to the [[ picker.
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).insertContent('[[').run()
      },
    },
    {
      key: 'mention',
      label: tr('slash.mention'),
      description: tr('slash.mentionDesc'),
      keywords: ['mention', 'person', 'user', 'people', '@'],
      icon: <AtSign className="h-4 w-4" />,
      group: 'moly',
      isAvailable: (editor) => hasExtension(editor, 'mention'),
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).insertContent('@').run()
      },
    },
    // --- Basic ---
    {
      key: 'text',
      label: tr('slash.text'),
      description: tr('slash.textDesc'),
      keywords: ['text', 'paragraph', 'plain'],
      icon: <Pilcrow className="h-4 w-4" />,
      group: 'basic',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setParagraph().run()
      },
    },
    ...headings.map(({ level, shortcut }) => ({
      key: `heading-h${level}`,
      label: tr('slash.heading', { level }),
      shortcut,
      keywords: ['heading', `heading ${level}`, `h${level}`, 'title'],
      icon: <span className="text-xs font-semibold uppercase text-foreground">H{level}</span>,
      group: 'basic' as const,
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).setNode('heading', { level }).run()
      },
    })),
    {
      key: 'bulleted-list',
      label: tr('slash.bulleted'),
      shortcut: '-',
      keywords: ['list', 'bullet', 'unordered'],
      icon: <ListIcon className="h-4 w-4" />,
      group: 'basic',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run()
      },
    },
    {
      key: 'numbered-list',
      label: tr('slash.numbered'),
      shortcut: '1.',
      keywords: ['list', 'number', 'ordered'],
      icon: <ListOrdered className="h-4 w-4" />,
      group: 'basic',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run()
      },
    },
    {
      key: 'todo-list',
      label: tr('slash.todo'),
      description: tr('slash.todoDesc'),
      shortcut: '[]',
      keywords: ['todo', 'to-do', 'checkbox', 'check', 'task list', 'checklist'],
      icon: <ListTodo className="h-4 w-4" />,
      group: 'basic',
      isAvailable: (editor) => hasExtension(editor, 'taskList'),
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleTaskList().run()
      },
    },
    {
      key: 'toggle',
      label: tr('slash.toggle'),
      description: tr('slash.toggleDesc'),
      keywords: ['toggle', 'collapse', 'collapsible', 'details', 'fold', 'expand'],
      icon: <ChevronRight className="h-4 w-4" />,
      group: 'basic',
      isAvailable: (editor) => hasExtension(editor, 'details'),
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setDetails().run()
      },
    },
    // --- Blocks ---
    {
      key: 'callout',
      label: tr('slash.callout'),
      description: tr('slash.calloutDesc'),
      keywords: ['callout', 'note', 'info', 'tip', 'warning', 'alert', 'highlight'],
      icon: <MessageSquareWarning className="h-4 w-4" />,
      group: 'blocks',
      isAvailable: (editor) => hasExtension(editor, 'callout'),
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setCallout().run()
      },
    },
    {
      key: 'blockquote',
      label: tr('slash.quote'),
      shortcut: '>',
      keywords: ['quote', 'blockquote', 'citation'],
      icon: <Quote className="h-4 w-4" />,
      group: 'blocks',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().run()
      },
    },
    {
      key: 'code-block',
      label: tr('slash.code'),
      shortcut: '```',
      keywords: ['code', 'snippet', 'pre'],
      icon: <CodeIcon className="h-4 w-4" />,
      group: 'blocks',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run()
      },
    },
    {
      key: 'table',
      label: tr('slash.table'),
      keywords: ['table', 'grid', 'row', 'column', 'cell'],
      icon: <TableIcon className="h-4 w-4" />,
      group: 'blocks',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
      },
    },
    {
      key: 'divider',
      label: tr('slash.divider'),
      shortcut: '---',
      keywords: ['divider', 'separator', 'line', 'rule', 'hr'],
      icon: <Minus className="h-4 w-4" />,
      group: 'blocks',
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).setHorizontalRule().run()
      },
    },
  ]
}

/**
 * Every slash-command key. Callers that need to *subtract* an item (rather
 * than list the handful they want) start from this — see the editor's
 * `enableTables` gate, which drops `table` when the table extension isn't
 * registered and the command would therefore be a no-op.
 */
export const SLASH_COMMAND_KEYS: Array<string> = createSlashMenuItems().map((item) => item.key)

function SlashCommandMenu({
  menuItems,
  selectedIndex,
  onSelect,
  onHover,
}: {
  menuItems: Array<SlashCommandItem>
  selectedIndex: number
  onSelect: (index: number) => void
  onHover: (index: number) => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [flipUp, setFlipUp] = useState(false)

  useLayoutEffect(() => {
    const menu = menuRef.current
    if (!menu) return
    const rect = menu.getBoundingClientRect()
    const PADDING = 8
    setFlipUp(rect.bottom > window.innerHeight - PADDING)
  }, [menuItems.length])

  useLayoutEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  return (
    <div
      ref={menuRef}
      role="listbox"
      aria-label={tr('slash.ariaLabel')}
      className={cn(
        'z-50 max-h-[360px] w-[290px] overflow-y-auto rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-lg',
        flipUp ? 'mb-3' : 'mt-3',
      )}
      style={flipUp ? { transform: 'translateY(calc(-100% - 1.5rem))' } : undefined}
    >
      {menuItems.map((item, index) => {
        const isSelected = selectedIndex === index
        const startsGroup = index === 0 || menuItems[index - 1]?.group !== item.group
        return (
          <Fragment key={item.key}>
            {startsGroup ? (
              <p
                className={cn(
                  'px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                  index === 0 ? 'pt-1' : 'mt-1 border-t pt-2',
                )}
              >
                {tr(`groups.${item.group}`)}
              </p>
            ) : null}
            <button
              type="button"
              ref={(el) => {
                itemRefs.current[index] = el
              }}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                isSelected ? 'bg-secondary text-foreground' : 'hover:bg-secondary/60',
              )}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => onHover(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault()
                onSelect(index)
              }}
            >
              {item.icon ? (
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-card text-[10px] font-semibold',
                    item.group === 'moly' ? 'text-brand' : 'text-foreground',
                  )}
                >
                  {item.icon}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-foreground">{item.label}</span>
                {item.description ? (
                  <span className="block truncate text-[11px] text-muted-foreground">{item.description}</span>
                ) : null}
              </span>
              {item.shortcut ? (
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{item.shortcut}</span>
              ) : null}
            </button>
          </Fragment>
        )
      })}
    </div>
  )
}

function SlashCommandMenuWrapper(props: SuggestionProps<SlashCommandItem>) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const handleSelect = useCallback(
    (index: number) => {
      const item = props.items[index]
      if (item) {
        props.command(item)
      }
    },
    [props],
  )

  // Expose keyboard handlers to the suggestion plugin
  // This is done via the component ref pattern
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
        setSelectedIndex((prev) =>
          prev <= 0 ? props.items.length - 1 : prev - 1,
        )
        return true
      }

      if (event.key === 'ArrowDown' || event.key === 'Tab') {
        setSelectedIndex((prev) =>
          prev >= props.items.length - 1 ? 0 : prev + 1,
        )
        return true
      }

      if (event.key === 'Enter') {
        handleSelect(selectedIndex)
        return true
      }

      return false
    },
    [props.items.length, selectedIndex, handleSelect],
  )

  // Store onKeyDown on the element so the suggestion plugin can access it
  const wrapperRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (el) {
      ;(el as any).__onKeyDown = onKeyDown
    }
  }, [onKeyDown])

  // Reset selected index when items change
  useLayoutEffect(() => {
    setSelectedIndex(0)
  }, [props.items])

  if (props.items.length === 0) {
    return null
  }

  return (
    <div ref={wrapperRef}>
      <SlashCommandMenu
        menuItems={props.items}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        onHover={setSelectedIndex}
      />
    </div>
  )
}

function createSuggestionConfig(
  allowedKeys?: Array<string>,
): Omit<SuggestionOptions<SlashCommandItem>, 'editor'> {
  return {
  char: '/',
  allowSpaces: true,
  startOfLine: false,
  items: ({ query, editor }) => {
    const items = createSlashMenuItems().filter(
      (item) =>
        (!allowedKeys || allowedKeys.includes(item.key)) &&
        (!item.isAvailable || item.isAvailable(editor)),
    )
    if (!query) return items
    const lower = query.toLowerCase()
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(lower) ||
        item.keywords.some((kw) => kw.includes(lower)),
    )
  },
  command: ({ editor, range, props: item }) => {
    item.command({ editor, range })
  },
  render: () => {
    let container: HTMLDivElement | null = null
    let root: ReturnType<typeof createRoot> | null = null

    return {
      onStart: (props) => {
        container = document.createElement('div')
        container.style.position = 'absolute'
        container.style.zIndex = '50'

        // Position near the cursor
        if (props.clientRect) {
          const rect = props.clientRect()
          if (rect) {
            container.style.left = `${rect.left}px`
            container.style.top = `${rect.bottom}px`
          }
        }

        document.body.appendChild(container)
        root = createRoot(container)
        root.render(<SlashCommandMenuWrapper {...props} />)
      },
      onUpdate: (props) => {
        if (container && props.clientRect) {
          const rect = props.clientRect()
          if (rect) {
            container.style.left = `${rect.left}px`
            container.style.top = `${rect.bottom}px`
          }
        }
        root?.render(<SlashCommandMenuWrapper {...props} />)
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          root?.unmount()
          container?.remove()
          container = null
          root = null
          return true
        }

        // Delegate to the rendered component's key handler
        const el = container?.firstElementChild
        if (el && (el as any).__onKeyDown) {
          return (el as any).__onKeyDown(props.event)
        }

        return false
      },
      onExit: () => {
        root?.unmount()
        container?.remove()
        container = null
        root = null
      },
    }
  },
  }
}

export function createSlashCommandExtension(allowedKeys?: Array<string>) {
  return Extension.create({
    name: 'slashCommand',

    addOptions() {
      return {
        suggestion: createSuggestionConfig(allowedKeys),
      }
    },

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          ...this.options.suggestion,
        }),
      ]
    },
  })
}

export const SlashCommandExtension = createSlashCommandExtension()

export type { SlashCommandItem }
